// @ts-strict-ignore
import {
  AfterViewChecked,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
  Output,
  NgZone,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { LMDashboardPanelBaseComponent } from './lm-dashboard-panel-base.component';
import { LandmarkPanel } from '@form/lm-form.model';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMModel } from '@form/model/lm-model';
import * as urltemplate from 'url-template';
import { LMTextEvent } from '@field/text';
import {
  AppConstants,
  LMButtonType,
  LMGlobalUtilService
} from '@utils/index';
import { LMNavigationService } from '@navigation/lm-navigation.service';
import { DocumentResource } from '@resources/document.resource';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';
import { LMUrlInfoBuilderService } from '@services/lm-url-info-builder.service';
import { LMGlobalPersonalizationService } from '@app/states';
import { KeyedResource } from '@app/resources';
import { LMBusyIndicatorService, LMContextService, LMErrorUtilService, LMOverlayService } from '@app/services';
import { LMMenuLinkBoxService } from '../../services/lm-menu-link-box.service';
import { LMFormRequestService } from '@form/services/lm-form-request.service';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import ClickEvent = JQuery.ClickEvent;

@Component({
  selector:    'lm-dashboard-panel-form',
  standalone: false,
  templateUrl: './lm-dashboard-panel-form.component.html',
  styleUrls:   [ 'lm-dashboard-panel-form.component.scss' ]
})

export class LMDashboardPanelFormComponent extends LMDashboardPanelBaseComponent implements AfterViewChecked, OnInit {
  @HostBinding('attr.id') public id;
  @HostBinding('attr.data-automation-id') public automationId;

  @ViewChild(forwardRef(() => 'LMFormComponent')) lmFormComponent; // : LMFormComponent;

  @Output() componentDisplayed = new EventEmitter<any>();
  @Output() fieldEvent = new EventEmitter<LMFieldEvent>();
  
  @Input() formUrlInfo: LMFormUrlInfo;
  @Input() panel: LandmarkPanel;
  @Input() model: LMModel; // used by composite forms when displaying panels as dashboard panes
  @Input() specFields: LMSpecFields; // used by composite forms when displaying panels as dashboard panes
  @Input() placeHolder: ViewContainerRef; // used by composite forms when displaying panels as dashboard panes
  @Input() messageRuleView: LMDataView;

  @Input() set fromDataView(dataView: LMDataView) {
    this._fromDataView = dataView;
    this._fromDataViewChanged = true;
  }

  get fromDataView() {
    return this._fromDataView;
  }

  private _fromDataView: LMDataView;
  private _fromDataViewChanged: boolean;

  public initDataView: LMDataView = null;
  public overNavigationLink: string;
  public hasOverviewNavigation = false;
  public displayLinkAsText = false;
  public isInAccessibilityMode: boolean;
  public isMenuPanel = false;
  public isMenuLinkBox = false;
  public links: Array<LinkInfo> = [];
  public showMenuLinkPanel = false;
  public widgetClasses: string;
  public drillAroundWindow = false;

  public useBackgroundImage = false;
  public backgroundImageUrl: SafeStyle;

  constructor(
    private lmContextService: LMContextService,
    protected lmUrlUtilService: LMUrlUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    protected lmNavigationService: LMNavigationService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmUniqueIdService: LMUniqueIdService,
    protected lmThemeService: LMThemeService,
    protected lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmMenuLinkBoxService: LMMenuLinkBoxService,
    private lmFormRequestService: LMFormRequestService,
    private _sanitizer: DomSanitizer,
    protected lmOverlayService: LMOverlayService,
    protected lmErrorUtilService: LMErrorUtilService,
    protected zone: NgZone
  ) {
    super(lmThemeService, lmOverlayService, lmUrlUtilService, lmNavigationService, lmErrorUtilService, zone);
  }

  ngOnInit() {
    this.isInAccessibilityMode = this.lmGlobalPersonalizationService.getAccessibilityMode();
    this.id = this.panel.id + AppConstants.ELEMENT_ID_SUFFIX_DASHBOARDPANEL;
    this.automationId = `lm-${this.panel.automationId}-dashboardpanel`;

    if (this.panel.type === 'menuPanel') {
      const menuPanelFormId = this.buildMenuPanelFormId();
      this.panel.formSpec = {
        automationId: this.panel.automationId,
        formElementId: menuPanelFormId,
        busFormSpec:   this.buildBusFormSpec(this.panel),
        busClassSpec:  this.buildBusClassSpec(),
        resourceId:    menuPanelFormId
      };
      this.formUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromFormSpec(this.panel.formSpec);
      this.initDataView = this.fromDataView;
      this.isMenuPanel = true;
    } else if (this.panel.type === 'MenuLinkBoxPanel') {
      this.isMenuLinkBox = true;

      this.links = this.lmMenuLinkBoxService.buildLinkMenuSpec(this.panel.layout, this._fromDataView);
      if (this.links.some((link: LinkInfo) => link.visible)) {
        this.showMenuLinkPanel = true;
      }

      // build width/height classes for the widget if they are set to double, triple or quad
      this.widgetClasses = '';
      if (!(this.panel.widgetWidth === undefined || this.panel.widgetWidth === 'single')) {
        this.widgetClasses += ' ' + this.panel.widgetWidth + '-width';
      }
      if (!(this.panel.widgetHeight === undefined || this.panel.widgetHeight === 'single')) {
        this.widgetClasses += ' ' + this.panel.widgetHeight + '-height';
      }

      this.lmBusyIndicatorService.hideBusyIndicator();
    } else if (this.panel.formSpec.dependentForm || this.panel.formSpec.independentForm) {
      // ------------------------------------------------------------------------------
      // if panel.formSpec is dependent then override main forms formUrlInfo that is
      // passed into this component.
      // ------------------------------------------------------------------------------
      this.formUrlInfo = this.lmUrlInfoBuilderService.getFormUrlInfoFromFormSpec(this.panel.formSpec, { isArchiveData: this.panel.isArchiveData });
    }

    // --------------------------------------------------------------------------------
    // use the formUrlInfo object passed in. this.formUrlInfo is the main form
    // formUrlInfo object as should be used for form panels that have a dashboard-view
    // with this lm-dashboard-panel-form.
    // Exception to this are menu panels (only allowed on pages), dependent
    // (aka related) panels, and independent form panels.
    // --------------------------------------------------------------------------------
    if (!this.formUrlInfo && this.panel.type !== 'MenuLinkBoxPanel') {
      console.error('formUrlInfo is null for this lm-dashboard-panel-form');
    }

    if (this.lmContextService.drillAroundWindow()) {
      this.drillAroundWindow = true;
    }



    this.build();
  }

  ngAfterViewChecked() {
    if (this._fromDataViewChanged) {
      this._fromDataViewChanged = false;
      this.build();
    }
  }

  saveDashboardPanelState(): void {
    if (this.lmFormComponent) {
      this.lmFormComponent.saveComponentState(true);
    }
  }

  clearDashboardPanelState(): void {
    if (this.lmFormComponent) {
      this.lmFormComponent.clearComponentState(true);
    }
  }

  onNavigationClick($event: ClickEvent): void {
    if (this.lmFormComponent) {
      const navigation: LMOverviewNavigation = this.panel.overviewNavigation ? this.panel.overviewNavigation : this.panel.busFormPanel.overviewNavigation;

      let flyoutOriginatingEvent: LMFormOverlayClickedEvent = null;

      const flyoutEnabled = this.lmContextService.allowFlyoutLinks();
      if (flyoutEnabled && navigation.openInOverlay) {
        flyoutOriginatingEvent = {
          event: $event,
          row: 0
        };

      }

      this.runOverviewNavigation(navigation, flyoutOriginatingEvent);
    }
  }

  onFieldEvent(event: LMFieldEvent): void {
    if ('TextLinkEvent' === event.eventType) {
      const linkEvent = event as LMTextEvent;

      if (linkEvent.layoutObject.hasHttpUrl || linkEvent.layoutObject.linkNavigation) {
        // this.buildFormTextLinkNavigation(linkEvent.layoutObject);
        // this.fieldEvent.emit(event);
        const dataView = this.fromDataView ? this.fromDataView : this.lmFormComponent.getDataView();
        const layoutObject =  linkEvent.layoutObject;

        this.lmFormRequestService.runHttpUrl(
          layoutObject.httpUrl,
          !!layoutObject.openExternal,
          !!layoutObject.isLazyUrlRetrieval,
          layoutObject.httpUrl?.variables,
          layoutObject.fieldId,
          dataView,
          this.formUrlInfo.formRequestUrl,
          dataView?.resourceId
        );
      } else if (linkEvent.layoutObject.type === LMButtonType.NAVIGATION) {
        this.runNavigation(linkEvent.layoutObject.navigation, linkEvent.layoutObject.showAsPdf);
      } else if (linkEvent.layoutObject.type === LMButtonType.VIEWDOCUMENT) {
        this.openDocumentResource(linkEvent.layoutObject.resourceId);
      } else if (linkEvent.layoutObject.type === LMButtonType.REGISTRATION) {
        this.lmGlobalUtilService.navigateByUrl(linkEvent.layoutObject.registerLink);
      } else if (linkEvent.layoutObject.type === LMButtonType.SIGNIN) {
        this.lmGlobalUtilService.navigate(linkEvent.layoutObject.signInLink);
      } else if (linkEvent.layoutObject.type === LMButtonType.SIGNOUT) {
        this.lmGlobalUtilService.navigate(linkEvent.layoutObject.signOutLink);
      } else {
        this.fieldEvent.emit(event);
      }
    } else {
      this.fieldEvent.emit(event);
    }
  }

  build() {
    if (!this.drillAroundWindow
      && this.panel
      && this._fromDataView
      && (this.panel.overviewNavigation || (this.panel.busFormPanel && this.panel.busFormPanel.overviewNavigation))
    ) {
      this.hasOverviewNavigation = true;
      if ((this.panel.overviewNavigation && this.panel.overviewNavigation.label)
        || (this.panel.busFormPanel && this.panel.busFormPanel.overviewNavigation && this.panel.busFormPanel.overviewNavigation.label)
      ) {
        this.displayLinkAsText = true;
        this.overViewNavigationLabel();
      }
    }

    if (this.panel.background) {
      if (this.panel.background.useBackgroundImage) {
        this.useBackgroundImage = true;
        this.backgroundImageUrl = this._sanitizer.bypassSecurityTrustStyle(`url('${this.panel.background.image}')`);
      }
    }
  }

  overViewNavigationLabel() {
    const message = this.panel.overviewNavigation ? this.panel.overviewNavigation.label : this.panel.busFormPanel.overviewNavigation.label;
    const dataView = this.fromDataView ? this.fromDataView : this.messageRuleView;
    this.overNavigationLink = this.lmFormatUtilService.formatMessageRule(message, dataView);
  }

  onFormRendered() {
    this.componentDisplayed.emit({ panel: this.panel });
  }

  private runNavigation(navigation: string, showAsPdf: boolean) {
    let dataView;
    if (this.fromDataView) {
      dataView = this.fromDataView;
    } else {
      dataView = this.lmFormComponent.getDataView();
    }

    const compiledTemplateNavUrl = urltemplate.parse(this.formUrlInfo.navigationUrl);
    const navigationUrl = compiledTemplateNavUrl.expand({
      resourceId:     dataView.resourceId,
      navigationName: navigation
    });
    this.lmNavigationService.runNavigation(navigationUrl, undefined, { showAsPdf });
  }

  private runOverviewNavigation(navigation: LMOverviewNavigation, flyoutOriginatingEvent: LMFormOverlayClickedEvent): void {
    let dataView;
    if (this.fromDataView) {
      dataView = this.fromDataView;
    } else {
      dataView = this.lmFormComponent.getDataView();
    }

    const compiledUrlTemplate = urltemplate.parse(this.formUrlInfo.navigationUrl);
    const url = compiledUrlTemplate.expand({ resourceId: dataView.resourceId, navigationName: navigation.name });

    if (this.lmContextService.allowFlyoutLinks() && navigation.openInOverlay) {
      this.openNavigationInOverlay(url, navigation.name, navigation.navigationTargetComponent, flyoutOriginatingEvent);
    } else {
      this.lmGlobalUtilService.navigateByUrl(url);
    }
  }

  private buildFormTextLinkNavigation(link: LMButtonLayout) {
    if (link.httpUrl) {
      let dataView;
      if (this.fromDataView) {
        dataView = this.fromDataView;
      } else {
        dataView = this.lmFormComponent.getDataView();
      }

      const option: string = (link.openExternal) ? 'external' : '';
      let url: string;
      if (link.httpUrl && link.httpUrl?.variables && !link.httpUrl.hasContext) {
        url = this.lmFormatUtilService.formatMessageRule(link.httpUrl, dataView, undefined, false);
      } else if (dataView?.buttonLinks?.[link.fieldId]) {
        url = dataView.buttonLinks[link.fieldId];
      } else {
        url = this.lmFormatUtilService.formatMessageRule(link.httpUrl, dataView, undefined, false);
      }

      if (url) {
        // When getting a http url/linkback that returns a navigation, we need to handle
        // by calling runNavigation instead of navigate. This will prevent the navigation
        // url from replacing the current lmclient route.
        // see LMCLIENT-35609.
        let resource: KeyedResource;
        try {
          //getRouteResource will throw an error if the url cannot be parsed.
          resource = this.lmUrlUtilService.getRouteResource(url);
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          //noop
        }
        // copied from lm-navigation.component
        if (resource?.resourceType === 'navigation') {
          this.lmNavigationService.runNavigation(url, undefined, { openNewTab: link.openExternal });
        } else {
          this.lmGlobalUtilService.navigate(url, null, option);
        }
      }
    }
  }

  private openDocumentResource(resourceId: string): void {
    let dataView: LMDataView;
    if (this.fromDataView) {
      dataView = this.fromDataView;
    } else {
      dataView = this.lmFormComponent.getDataView();
    }
    const documentResource = new DocumentResource({
      resourceId,
      dataView
    }, '');
    const url: string = this.lmUrlUtilService.buildUrl({ resource: documentResource });
    this.lmGlobalUtilService.navigate(url, null, 'external');
  }

  buildBusFormSpec(args): LMBusFormSpec {

    const busFormLayoutSpec: LMBusFormLayoutSpec = {
      layout: args.layout
    };

    const messageRule: LMMessageRule = {
      resourceValue: '',
    };

    const busFormSpec: LMBusFormSpec = {
      busFormLayoutSpec,
      name:                     '',
      fields:                   {},
      title:                    messageRule,
      formType:                 'Menu',
      effectiveDateField:       {},
      effectiveDateFieldId:     '',
      reasonCodeFieldId:        '',
      actionCommentFieldId:     '',
      effectiveThroughFieldId:  '',
      correctionCommentFieldId: '',
      openActionRestricted:     false,
      implementsDashBoardView:  true,
    };
    return busFormSpec;
  }

  buildBusClassSpec(): LMBusClassSpec {

    const busClassSpec: LMBusClassSpec = {
      actions:                [],
      actionSpec:             {},
      implAuditing:           false,
      implEffectiveDated:     false,
      isBusClass:             false,
      isBusTask:              false,
      name:                   'menuPanel',
      implementsOrgChartView: false,
    };
    return busClassSpec;
  }

  onLinkClicked(link: LinkInfo): void {
    this.lmMenuLinkBoxService.runMenuLinkBoxAction(link, this.placeHolder);
  }

  private buildMenuPanelFormId() {
    return this.lmUniqueIdService.uniqueId([ this.panel.id, 'MenuPanel' ]);
  }
}
